// components/forms/EnquiryForm.js
import React, { useState, useEffect } from "react";
import { createEnquiry, updateStatusEnquiry, updateEnquiry } from "../../services/EnquiryService.js";
import { getAllAgents } from "../../services/AgentService.js";
import { getAllCourses } from "../../services/CourseService.js";
import { CheckCircle } from "lucide-react";

function EnquiryForm({ onCancel, initialData, isPublic = false, showSubmit = false }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    studentId: "",
    dateOfBirth: "",
    email: "",
    phone: "",
    address: "",
    usi: "",
    courseEnrolledIn: "",
    informationRequests: [],
    otherInformationRequest: "",
    question: "",
    courses: [],
    agent: "",
    customAgent: "",
    status: "new", // always "new"
    notes: "",
  });

  const [agents, setAgents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch agents
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const data = await getAllAgents();
        const filteredAgents = isPublic ? data.filter(agent =>
          agent.status.toLowerCase() === "active" && !agent.agentName.toLowerCase().includes("test")
        ) : data;
        setAgents(filteredAgents || []);
      } catch (error) {
        alert("Failed to load agents");
      }
    };
    fetchAgents();
  }, []);

  // Fetch courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const allCourses = await getAllCourses();
        const filteredCourses = allCourses.filter(
            (c) => c.status.toLowerCase() === "active" && !c.courseName.toLowerCase().includes("test")
          );
        setCourses(filteredCourses || []);
      } catch (error) {
        alert("Failed to load courses");
      }
    };
    fetchCourses();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        firstName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        studentId: initialData.studentId || "",
        dateOfBirth: initialData.dateOfBirth || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        address: initialData.address || "",
        usi: initialData.usi || "",
        courseEnrolledIn: initialData.courseEnrolledIn || "",
        informationRequests: initialData.informationRequests || [],
        otherInformationRequest: initialData.otherInformationRequest || "",
        question: initialData.question || "",
        courses: initialData.courses || [],
        agent: initialData.agent || "",
        customAgent: initialData.customAgent || "",
        status: "new",
        notes: initialData.notes || "",
      });
    }
  }, [initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCourseChange = (courseName) => {
    setFormData((prev) => {
      const selected = new Set(prev.courses);
      if (selected.has(courseName)) {
        selected.delete(courseName);
      } else {
        selected.add(courseName);
      }
      return { ...prev, courses: Array.from(selected) };
    });
  };

  const informationRequestOptions = [
    "Certificate",
    "Completion Letter",
    "Course Progress",
    "Invitation Letter",
    "Attendance",
    "Fee Letter",
    "Early Graduation",
    "Statement of Attainment",
    "Concession/Completion Letter",
    "Other",
  ];

  const handleInformationRequestChange = (requestName) => {
    setFormData((prev) => {
      const selected = new Set(prev.informationRequests);

      if (selected.has(requestName)) {
        selected.delete(requestName);
      } else {
        selected.add(requestName);
      }

      return {
        ...prev,
        informationRequests: Array.from(selected),
        ...(requestName === "Other" && selected.has("Other") === false
          ? { otherInformationRequest: "" }
          : {}),
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const finalData = {
        ...formData,
        agent:
          formData.agent === "other" ? formData.customAgent : formData.agent,
        status: "new",
      };

      if (initialData?.id) {
        await updateEnquiry(initialData.id, finalData);
      } else {
        await createEnquiry(finalData);
      }

      setSubmitted(true);
    } catch (error) {
      alert("Failed to save enquiry. Please try again.");
    }
    finally {
      setLoading(false);
    }
  };

  // ----------------------------
  // Success Page
  // ----------------------------
 if (submitted && isPublic) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        minHeight: "100vh",
        padding: "2rem 1rem",
        background: "#f5f5f5",
      }}
    >
      {/* Banner */}
      <div style={{ width: "100%", maxWidth: "700px", marginBottom: "1rem" }}>
        <img
          style={{ width: "100%", maxHeight: "200px", objectFit: "contain" }}
          src={require("../../images/bannar.png")}
          alt="CIET Logo"
        />
      </div>

      {/* Success Card */}
      <div
        style={{
          maxWidth: "600px",
          width: "100%",
          padding: "2rem",
          textAlign: "center",
          borderRadius: "12px",
          background: "#fff",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        <CheckCircle
          size={64}
          color="#10b981"
          style={{ marginBottom: "1.5rem" }}
        />
        <h1 style={{ marginBottom: "1rem" }}>
          Enquiry Submitted Successfully!
        </h1>
        <p style={{ fontSize: "1.1rem", color: "#555" }}>
          Thank you for your enquiry.{" "}
          A team member from{" "}
          <strong>
            Canberra Institute of Education and Technology (CIET)
          </strong>{" "}
          will contact you shortly to help resolve your questions.
        </p>

        {isPublic && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "2rem",
            }}
          >
            <button
              className="btn btn-primary"
              onClick={() => (window.location.href = "/enquire-now")}
            >
              Submit Another Enquiry
            </button>
          </div>
        )}
        
      </div>
    </div>
  );
}
if (submitted && !isPublic) {
    onCancel();
    return null;
}


  // ----------------------------
  // Enquiry Form
  // ----------------------------
  return (
    <div
      className="form-container"
      style={{
        width: "auto",
        margin: isPublic
          ? "3rem clamp(16px, 5vw, 100px)"
          : "0",
        padding: isPublic ? "3rem 2rem" : "2rem",
        boxSizing: "border-box",
        borderRadius: isPublic ? "12px" : "0",
        boxShadow: isPublic
          ? "0 4px 12px rgba(0,0,0,0.1)"
          : "none",
        background: isPublic ? "#fff" : "transparent",
      }}
    >
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: isPublic ? "12px" : "0"
          }}
        >
          <div className="spinner" style={{ textAlign: "center" }}>
            <div className="loader" style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 12px",
              
            }} />
            
          </div>
          <p style={ {
            padding: "0.5rem",
          }
          }>Submitting, please wait!</p>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div
          className="actions"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: "2rem",
          }}
        >
          {isPublic && (
            <div style={{ display: "flex", gap: "1rem" }}>
                <img
                style={{ height: "4rem", width: "auto" }}
                src={require("../../images/logo.png")}
                alt="CIET Logo"
                />
                <h1 className="page-title" style={{ margin: 0, alignSelf: "center" }}>
                Enquiry Application
                </h1>
          </div>
          )}
          {!isPublic && (
            <div>
                <h1 className="page-title">Student Enquiry Application</h1>
                <p className="page-subtitle">Create a new application</p>
            </div>
          )}

          {/* Buttons */}
          {!isPublic && (
            <div style={{ display: "flex", gap: "1rem" }}>
            {!isPublic && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                {initialData?.status !== "resolved" ? "Cancel" : "Back"}
              </button>)
              
            }
            {(showSubmit) && (
                  <div style={{ display: "flex", gap: "1rem", justifyContent: "center"}}>
                      <button type="submit" className="btn btn-primary">
                          Submit Enquiry
                      </button>
                  </div>
              )}
            {/* <button type="submit" className="btn btn-primary">
            Submit Enquiry
            </button> */}
            {/* Status Update Button for internal users */}
            {!isPublic && initialData?.id && initialData?.status !== "resolved" && (
            <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                    try {
                    await updateStatusEnquiry(initialData.id, initialData, "resolved");
                    alert("Enquiry marked as resolved!");
                    setFormData((prev) => ({ ...prev, status: "resolved" }));
                    if (onCancel) onCancel(); // call cancel after user clicks OK
                    } catch (err) {
                    alert("Failed to update status. Please try again.");
                    }
                }}
                >
                Mark as Resolved
            </button>

            )}

          </div>
            )}
          
        </div>

        {/* Student Information */}
        <div className="form-section" style={{ marginBottom: "2rem" }}>
            <h2 style={{ marginBottom: isPublic ? "1rem" : "0.5rem" }}>Student Information</h2>
            <hr style={{ margin: isPublic ? "1.5rem 0" : "1rem 0" }} />

          <div className="form-grid" style={{ marginBottom: isPublic ? "1.5rem" : "1rem" }}>
            {[
              { label: "First Name", name: "firstName", required: true },
              { label: "Last Name", name: "lastName", required: true },
              { label: "Student ID", name: "studentId" },
              { label: "Date of Birth", name: "dateOfBirth", type: "date" },
              { label: "Email", name: "email", type: "email", required: true },
              { label: "Phone Number", name: "phone", type: "tel", required: true },
              { label: "Address", name: "address" },
              { label: "Unique Student Identifier (USI)", name: "usi" },
            ].map(({ label, name, type = "text", required = false }) => (
              <div
                key={name}
                className="form-group"
                style={{ marginBottom: "1rem" }}
              >
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.75rem",
                    fontWeight: "600",
                  }}
                >
                  {label}
                </label>
                <input
                  type={type}
                  name={name}
                  value={formData[name]}
                  onChange={handleInputChange}
                  className="form-input"
                  required={required}
                />
              </div>
            ))}
          </div>

          <div className="form-group" style={{ marginBottom: "1rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.75rem",
                fontWeight: "600",
              }}
            >
              Course Enrolled In
            </label>
            <select
              name="courseEnrolledIn"
              value={formData.courseEnrolledIn}
              onChange={handleInputChange}
              className="form-input"
            >
              <option value="">-- Select Course --</option>
              {courses.map((course) => (
                <option key={course.id} value={course.courseName}>
                  {course.courseName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Enquiry Details */}
        <div className="form-section" style={{ marginBottom: "2rem" }}>
          <h2>Enquiry Details</h2>
          <hr style={{ margin: "1rem 0" }} />

          <div className="form-group" style={{ marginBottom: "2rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "600",
              }}
            >
              Request for Information
            </label>
            <p style={{ marginTop: 0, marginBottom: "1rem", color: "#555" }}>
              Please tick each item you are after. Requests are processed within ten working days after submission.
            </p>

            <div
              className="form-checkbox-group"
              style={{
                display: "grid",
                gridTemplateColumns: isPublic ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
                gap: "1rem 2rem",
                padding: "1rem",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
              }}
            >
              {informationRequestOptions.map((requestName) => (
                <label
                  key={requestName}
                  className="form-checkbox"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.informationRequests.includes(requestName)}
                    onChange={() => handleInformationRequestChange(requestName)}
                  />
                  {requestName === "Other" ? "Other (Please specify below)" : requestName}
                </label>
              ))}
            </div>
          </div>

          {formData.informationRequests.includes("Other") && (
            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.75rem",
                  fontWeight: "600",
                }}
              >
                Other Information Required
              </label>
              <textarea
                name="otherInformationRequest"
                value={formData.otherInformationRequest}
                onChange={handleInputChange}
                className="form-textarea"
                rows="2"
                placeholder="Please specify the information you require"
                required
              />
            </div>
          )}

          <div className="form-group" style={{ marginBottom: "1.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.75rem",
                fontWeight: "600",
              }}
            >
              Ask your question here
            </label>
            <textarea
              name="question"
              value={formData.question}
              onChange={handleInputChange}
              className="form-textarea"
              rows="2"
              placeholder="E.g. Can I continue from Painting to Carpentry?"
            />
          </div>

          <div className="form-group" style={{ marginBottom: "2rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.75rem",
                fontWeight: "600",
              }}
            >
              Courses Interested In
            </label>

            <div
              className="form-checkbox-group"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "1rem 2rem",
                padding: "1rem",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
              }}
            >
              {courses.map((course) => (
                <label
                  key={course.id}
                  htmlFor={`course-${course.id}`}
                  className="form-checkbox"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    id={`course-${course.id}`}
                    checked={formData.courses.includes(course.courseName)}
                    onChange={() => handleCourseChange(course.courseName)}
                  />
                  {course.courseName}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: "1.5rem" }}>
            <label
                style={{
                display: "block",
                marginBottom: "0.75rem",
                fontWeight: "600",
                }}
            >
                Agent
            </label>
            <select
                name="agent"
                value={formData.customAgent ? "other" : formData.agent}
                onChange={handleInputChange}
                className="form-input"
            >
                <option value="">-- Select Agent --</option>
                {agents.map((agent) => (
                <option key={agent.id} value={agent.agentName}>
                    {agent.agentName}
                </option>
                ))}
                <option value="other">Other</option>
            </select>
            </div>

            {/* Show custom agent input if "other" is selected or customAgent already has a value */}
            {(formData.agent === "other" || formData.customAgent) && (
            <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                <label
                style={{
                    display: "block",
                    marginBottom: "0.75rem",
                    fontWeight: "600",
                }}
                >
                Other Agent Name
                </label>
                <input
                type="text"
                name="customAgent"
                value={formData.customAgent}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter agent name"
                required
                />
            </div>
            )}
        </div>


        {/* Additional Notes */}
        <div className="form-section" style={{ marginBottom: "2rem" }}>
          <h2>Additional Notes</h2>
          <hr style={{ margin: "1rem 0" }} />
          <div className="form-group">
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              className="form-textarea"
              rows="3"
              placeholder="Any additional notes..."
            />
          </div>
        </div>
        {isPublic && (
            <div style={{ display: "flex", gap: "1rem", justifyContent: "center"}}>
                <button type="submit" className="btn btn-primary">
                    Submit Enquiry
                </button>
            </div>
        )}
      </form>
    </div>
  );
}

export default EnquiryForm;